import type { GridApi } from '@mui/x-data-grid-pro';
import { useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import type {
  ActualCostCompletenessStatus,
  ItemFinancials,
} from '@/common/api/types/itemFinancials';
import type { DatasetPage } from '@/common/hooks/useDatasetPages';
import { commonQueryKeys } from '@/common/queryKeys/common';
import { financeQueryKeys } from '@/common/queryKeys/finance';
import { actualCostRefreshSignal } from '@/common/sse/actualCostRefreshSignal';
import { associateByItemId } from '@/common/utils/associateById';
import { toNullableNumber } from '@/common/utils/number';
import { ItemFinancialsApi } from '@/features/financials/api/item-financials';
import { ItemFinancialsAggregateApi } from '@/features/financials/api/item-financials-aggregate';
import { buildIdKeyMatcher } from '../hooks/buildIdKeyMatcher';
import { useCrossCachePagesSync } from '../hooks/useCrossCachePagesSync';
import { type PageData, pageQueryKey, useTablePagedQuery } from '../hooks/useTablePagedQuery';

export type ActualCostEntry = {
  value: string | undefined;
  status: ActualCostCompletenessStatus | undefined;
};

type ItemFinancialsFields = Pick<ItemFinancials, 'budgetAmount' | 'forecastRevenueAmount'> & {
  // CapEx % is only ever meaningful at an item hierarchy's root — this is the reporting-store
  // resolved value (root's own, or inherited), never the raw stored value from item-store, which
  // may still hold a preserved-but-ignored figure on a descendant.
  capexPercentage: number | null;
  actualCostValue: string | undefined;
  actualCostStatus: ActualCostCompletenessStatus | undefined;
};

const EMPTY_FINANCIALS: ReadonlyMap<number, ItemFinancialsFields> = new Map();
const matchItemFinancialsKey = buildIdKeyMatcher(financeQueryKeys.itemFinancials.byItemId);

const pickFinancialsFields = (
  item: ItemFinancials,
  effectiveCapexPercentage: number | null,
  effectiveForecastRevenue: string | null,
  actualCostValue: string | undefined,
  actualCostStatus: ActualCostCompletenessStatus | undefined,
): ItemFinancialsFields => ({
  budgetAmount: item.budgetAmount,
  capexPercentage: effectiveCapexPercentage,
  forecastRevenueAmount: effectiveForecastRevenue,
  actualCostValue,
  actualCostStatus,
});

// item-store's raw item-financials cache (what this syncs from) has no notion of the
// reporting-store-resolved effective CapEx %, effective forecast revenue, or the rolled-up
// actual cost — carry the currently displayed values forward instead of blanking them whenever
// budget/revenue change elsewhere.
const itemFinancialsFromSource = (
  _id: number,
  sourceData: ItemFinancials | undefined,
  current: ItemFinancialsFields | undefined,
): ItemFinancialsFields | undefined =>
  sourceData
    ? pickFinancialsFields(
        sourceData,
        current?.capexPercentage ?? null,
        current?.forecastRevenueAmount ?? null,
        current?.actualCostValue,
        current?.actualCostStatus,
      )
    : undefined;

type VisibleItemFinancialsContextType = {
  financialsByItemId: ReadonlyMap<number, ItemFinancialsFields>;
};

const VisibleItemFinancialsContext = React.createContext<
  VisibleItemFinancialsContextType | undefined
>(undefined);

const useVisibleItemFinancialsField = <K extends keyof ItemFinancialsFields>(
  itemId: number | undefined,
  field: K,
): NonNullable<ItemFinancialsFields[K]> | undefined => {
  const context = React.useContext(VisibleItemFinancialsContext);
  if (!context || itemId === undefined) return undefined;
  return context.financialsByItemId.get(itemId)?.[field] ?? undefined;
};

export const useVisibleBudget = (itemId: number | undefined): string | undefined =>
  useVisibleItemFinancialsField(itemId, 'budgetAmount');

export const useVisibleCapexPercentage = (itemId: number | undefined): number | undefined =>
  useVisibleItemFinancialsField(itemId, 'capexPercentage');

export const useVisibleForecastRevenue = (itemId: number | undefined): string | undefined =>
  useVisibleItemFinancialsField(itemId, 'forecastRevenueAmount');

export const useVisibleActualCost = (itemId: number | undefined): ActualCostEntry | undefined => {
  const context = React.useContext(VisibleItemFinancialsContext);
  if (!context || itemId === undefined) return undefined;
  const entry = context.financialsByItemId.get(itemId);
  if (!entry || (entry.actualCostValue === undefined && entry.actualCostStatus === undefined)) {
    return undefined;
  }
  return { value: entry.actualCostValue, status: entry.actualCostStatus };
};

type ItemFinancialsAggregateItem = Awaited<
  ReturnType<typeof ItemFinancialsAggregateApi.getItemFinancialsAggregate>
>[number];

const primeAggregateCache = (
  queryClient: ReturnType<typeof useQueryClient>,
  aggregates: readonly ItemFinancialsAggregateItem[],
) => {
  for (const aggregateItem of aggregates) {
    // Lets useItemFinancialsAggregateQuery (item panel, loop financials sheet) reuse this
    // fetch instead of re-requesting the same item's aggregate when its own panel opens.
    queryClient.setQueryData(
      financeQueryKeys.itemFinancialsAggregate.bulk([aggregateItem.itemId]),
      [aggregateItem],
    );
  }
};

export const VisibleItemFinancialsProvider = ({
  pages,
  visiblePageIndices,
  pageIndexById,
  datasetSignature,
  apiRef,
  instanceKey,
  enabled = true,
  includeBudgetRevenue,
  includeCapex,
  includeForecastRevenue,
  includeActualCost,
  children,
}: {
  pages: readonly DatasetPage[];
  visiblePageIndices: readonly number[];
  pageIndexById: ReadonlyMap<number, number>;
  datasetSignature: string;
  apiRef: React.RefObject<GridApi>;
  instanceKey: string;
  enabled?: boolean;
  includeBudgetRevenue: boolean;
  includeCapex: boolean;
  includeForecastRevenue: boolean;
  includeActualCost: boolean;
  children: React.ReactNode;
}) => {
  const queryClient = useQueryClient();
  const needsAggregate = includeCapex || includeActualCost || includeForecastRevenue;

  const fetchByIds = React.useCallback(
    async (ids: readonly number[], signal: AbortSignal) => {
      const [financials, aggregates] = await Promise.all([
        includeBudgetRevenue
          ? ItemFinancialsApi.bulkGetItemFinancials([...ids], signal)
          : Promise.resolve([]),
        needsAggregate
          ? ItemFinancialsAggregateApi.getItemFinancialsAggregate([...ids], signal)
          : Promise.resolve([]),
      ]);
      primeAggregateCache(queryClient, aggregates);
      const aggregateByItemId = associateByItemId(aggregates);

      const financialsById = new Map<number, ItemFinancialsFields>();
      for (const item of financials) {
        const aggregate = aggregateByItemId[item.itemId];
        financialsById.set(
          item.itemId,
          pickFinancialsFields(
            item,
            toNullableNumber(aggregate?.effectiveCapexPercentage),
            aggregate?.effectiveForecastRevenue ?? null,
            aggregate?.rolledActualCost ?? undefined,
            aggregate?.rolledActualCostStatus ?? undefined,
          ),
        );
        queryClient.setQueryData(financeQueryKeys.itemFinancials.byItemId(item.itemId), item);
      }
      // A child that has never had its own item-financials row still inherits an effective
      // CapEx % / forecast revenue from its root (and may still have a rolled actual cost), so it
      // needs an entry here even though item-store's bulk-fetch omitted it entirely.
      for (const aggregateItem of aggregates) {
        if (financialsById.has(aggregateItem.itemId)) continue;
        const effectiveCapexPercentage = toNullableNumber(aggregateItem.effectiveCapexPercentage);
        const effectiveForecastRevenue = aggregateItem.effectiveForecastRevenue ?? null;
        const actualCostValue = aggregateItem.rolledActualCost ?? undefined;
        const actualCostStatus = aggregateItem.rolledActualCostStatus ?? undefined;
        if (
          effectiveCapexPercentage == null &&
          effectiveForecastRevenue == null &&
          actualCostValue === undefined &&
          actualCostStatus === undefined
        ) {
          continue;
        }
        financialsById.set(aggregateItem.itemId, {
          budgetAmount: null,
          forecastRevenueAmount: effectiveForecastRevenue,
          capexPercentage: effectiveCapexPercentage,
          actualCostValue,
          actualCostStatus,
        });
      }
      return financialsById;
    },
    [queryClient, includeBudgetRevenue, needsAggregate],
  );

  const { loadedById } = useTablePagedQuery<ItemFinancialsFields>({
    providerName: commonQueryKeys.itemsTable.visibleItemFinancials,
    instanceKey,
    pages,
    visiblePageIndices,
    pageIndexById,
    datasetSignature,
    fetchByIds,
    enabled,
    apiRef,
  });

  // Pages already loaded before the aggregate became relevant here never fetched it —
  // fetchByIds's dependency change alone doesn't rerun an already-fresh page (staleTime:
  // Infinity), so force a genuine refetch of this instance's pages when that happens.
  const wasNeedsAggregateRef = React.useRef(needsAggregate);
  React.useEffect(() => {
    const aggregateJustEnabled = needsAggregate && !wasNeedsAggregateRef.current;
    wasNeedsAggregateRef.current = needsAggregate;
    if (!aggregateJustEnabled) return;
    void queryClient.resetQueries({
      predicate: (query) =>
        query.queryKey[0] === commonQueryKeys.itemsTable.visibleItemFinancials &&
        query.queryKey[1] === instanceKey,
    });
  }, [needsAggregate, instanceKey, queryClient]);

  useCrossCachePagesSync<ItemFinancialsFields, ItemFinancials>({
    providerName: commonQueryKeys.itemsTable.visibleItemFinancials,
    instanceKey,
    pageIndexById,
    datasetSignature,
    matchKey: matchItemFinancialsKey,
    transform: itemFinancialsFromSource,
  });

  const refreshVersion = React.useSyncExternalStore(
    actualCostRefreshSignal.subscribe,
    actualCostRefreshSignal.getSnapshot,
    actualCostRefreshSignal.getSnapshot,
  );
  const stateRef = React.useRef({
    instanceKey,
    datasetSignature,
    enabled,
    includeCapex,
    includeActualCost,
    includeForecastRevenue,
  });
  stateRef.current = {
    instanceKey,
    datasetSignature,
    enabled,
    includeCapex,
    includeActualCost,
    includeForecastRevenue,
  };

  // Version each page's aggregate data (capex % / actual cost) was last confirmed fresh at.
  // A page catches up the moment it's scrolled back into view if it missed a recompute while
  // off-screen — no page goes stale forever — without refetching every subscribed page
  // (visible or not) on every recompute. `loadedById` decouples "this page's own load just
  // landed" from "a recompute just happened" — without it, the two can coincide on the same
  // render and a genuine recompute gets mistaken for a page's first sighting.
  const pageVersionRef = React.useRef<Map<number, number>>(new Map());

  // biome-ignore lint/correctness/useExhaustiveDependencies: loadedById isn't read directly, but its change decouples "a page's own load just landed" from "a recompute just happened" — see the comment above.
  React.useEffect(() => {
    const {
      instanceKey,
      datasetSignature,
      enabled,
      includeCapex,
      includeActualCost,
      includeForecastRevenue,
    } = stateRef.current;
    if (!enabled || (!includeCapex && !includeActualCost && !includeForecastRevenue)) return;

    const keyForPage = (pageIndex: number) =>
      pageQueryKey(
        commonQueryKeys.itemsTable.visibleItemFinancials,
        instanceKey,
        datasetSignature,
        pageIndex,
      );

    const stalePages = new Map<number, PageData<ItemFinancialsFields>>();
    for (const pageIndex of visiblePageIndices) {
      const previous = queryClient.getQueryData<PageData<ItemFinancialsFields>>(
        keyForPage(pageIndex),
      );
      if (!previous) continue; // not loaded yet — its own initial fetch will get fresh data

      const recordedVersion = pageVersionRef.current.get(pageIndex);
      if (recordedVersion === undefined) {
        // First time this page's own load has been seen complete — already as fresh as it
        // can be, nothing to catch up on yet.
        pageVersionRef.current.set(pageIndex, refreshVersion);
        continue;
      }
      if (recordedVersion === refreshVersion) continue;
      stalePages.set(pageIndex, previous);
    }
    if (stalePages.size === 0) return;

    const idsToFetch = Array.from(stalePages.values()).flatMap((page) => [
      ...page.valuesById.keys(),
      ...page.missingIds,
    ]);

    const controller = new AbortController();
    void (async () => {
      const aggregates = await ItemFinancialsAggregateApi.getItemFinancialsAggregate(
        idsToFetch,
        controller.signal,
      );
      if (controller.signal.aborted) return;
      primeAggregateCache(queryClient, aggregates);
      const aggregateByItemId = associateByItemId(aggregates);
      for (const [pageIndex, staleSnapshot] of stalePages) {
        const key = keyForPage(pageIndex);
        const current =
          queryClient.getQueryData<PageData<ItemFinancialsFields>>(key) ?? staleSnapshot;
        const valuesById = new Map(current.valuesById);
        const missingIds = new Set(current.missingIds);
        let changed = false;
        for (const id of [...staleSnapshot.valuesById.keys(), ...staleSnapshot.missingIds]) {
          const aggregate = aggregateByItemId[id];
          const value = valuesById.get(id);
          const nextCapexPercentage = includeCapex
            ? toNullableNumber(aggregate?.effectiveCapexPercentage)
            : (value?.capexPercentage ?? null);
          const nextForecastRevenueAmount = includeForecastRevenue
            ? (aggregate?.effectiveForecastRevenue ?? null)
            : (value?.forecastRevenueAmount ?? null);
          const nextActualCostValue = includeActualCost
            ? (aggregate?.rolledActualCost ?? undefined)
            : value?.actualCostValue;
          const nextActualCostStatus = includeActualCost
            ? (aggregate?.rolledActualCostStatus ?? undefined)
            : value?.actualCostStatus;
          if (
            value == null &&
            nextCapexPercentage == null &&
            nextForecastRevenueAmount == null &&
            nextActualCostValue === undefined &&
            nextActualCostStatus === undefined
          ) {
            continue;
          }
          if (
            value?.capexPercentage === nextCapexPercentage &&
            value?.forecastRevenueAmount === nextForecastRevenueAmount &&
            value?.actualCostValue === nextActualCostValue &&
            value?.actualCostStatus === nextActualCostStatus
          ) {
            continue;
          }
          valuesById.set(id, {
            budgetAmount: value?.budgetAmount ?? null,
            forecastRevenueAmount: nextForecastRevenueAmount,
            capexPercentage: nextCapexPercentage,
            actualCostValue: nextActualCostValue,
            actualCostStatus: nextActualCostStatus,
          });
          missingIds.delete(id);
          changed = true;
        }
        if (changed) {
          queryClient.setQueryData<PageData<ItemFinancialsFields>>(key, {
            ...current,
            valuesById,
            missingIds,
          });
        }
        pageVersionRef.current.set(pageIndex, refreshVersion);
      }
    })();

    return () => controller.abort();
  }, [refreshVersion, visiblePageIndices, queryClient, loadedById]);

  const value = React.useMemo<VisibleItemFinancialsContextType>(
    () => ({ financialsByItemId: loadedById.size > 0 ? loadedById : EMPTY_FINANCIALS }),
    [loadedById],
  );

  return (
    <VisibleItemFinancialsContext.Provider value={value}>
      {children}
    </VisibleItemFinancialsContext.Provider>
  );
};
